import axios from "axios"; // Import Axios
import React, { useEffect, useState } from "react";
import { Helmet } from 'react-helmet';
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import car from "../../src/images/progress/car.png";
import Navbar from "../components/navbar";
import Footer from "../homeComponents/footer";
import Loader from "../loader";
import Loading from "../loading";

const Progress = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [serviceDetails, setServiceDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL;

  const userId =
    useSelector((state) => state.auth.user?.id) ||
    location.state?.userId ||
    null;

    useEffect(() => {
      const fetchServiceDetails = async () => {
        try {
          const response = await axios.get(
            `${API_URL}/api/services/${userId}`
          );
    
          const services = response.data;
    
          if (services.length === 0) {
            // No services found for the user
            setError("Please create a service to continue");
            setServiceDetails([]); // Ensure no stale data remains
            return;
          }
    
          // Sort services by deliveryDate in ascending order
          const sortedServices = services
            .filter(service => service.deliveryDate) // Ensure deliveryDate exists
            .sort((a, b) => new Date(a.deliveryDate) - new Date(b.deliveryDate));
    
          // Prioritize services with "Assigned" status first, "Pending" status second
          const prioritizedServices = sortedServices.sort((a, b) => {
            if (a.status === "Assigned" && b.status !== "Assigned") return -1;
            if (a.status !== "Assigned" && b.status === "Assigned") return 1;
            if (a.status === "Pending" && b.status !== "Pending") return -1;
            if (a.status !== "Pending" && b.status === "Pending") return 1;
            return 0;
          });
    
          setServiceDetails(prioritizedServices);
          setError(""); // Clear any previous error messages
        } catch (err) {
          if (err.response && err.response.status === 404) {
            // Specific handling for 404 error
            setError("Service not found. Please create a service to continue.");
          } else {
            setError("Error fetching service details.");
          }
          // console.error("Fetch error:", err);
        } finally {
          setLoading(false);
        }
      };
    
      if (userId) {
        fetchServiceDetails();
      } else {
        setLoading(false);
      }
    }, [userId]);    

  const handleServiceClick = (serviceId) => {
    navigate(`/trackprogress/${serviceId}`);
  };

  return (
    <div className="font-clash">
            <Helmet>
              <title>Progress</title>
              <meta
                name="keywords"
                content="Pdi, Bengaluru, Bangalore, Kardo, Inspection, Ze, Pre delivery, Services, Car, Cars, Vaidya, BHP, Team, CID, Expert, In, Best, 24, BOSS, Dail, Just, New car, Checklist, Detailed, Report, Inspect, Reliable, Blog, Used car inspection, Buying a new car"
              />
              <meta
                name="description"
                content="Explore our reliable pre-delivery inspection services for new and used cars. Detailed reports, expert insights, and the best service in Bangalore and beyond."
              />
            </Helmet>      
      <Navbar />
      {/* Check for authentication and userId */}
      {!userId ? (
        <div className="flex items-center justify-center text-center bg-banner min-h-screen text-[#FFFF]">Please login to continue</div>
      ) : (
        <div className="flex flex-col items-center justify-center p-6 bg-banner min-h-screen py-10 pt-44">
        {loading ? (
                <div>
                <Loading />
              </div>
        ) : error ? (
          <p className="flex items-center justify-center text-center min-h-screen text-red-500">{error}</p>
        ) : (
          <>
            {/* First Section */}
            {serviceDetails.length > 0 ? (
              serviceDetails.map((service, index) => (
                <div
                  key={index}
                  onClick={() => handleServiceClick(service._id)}
                  className={`flex flex-col items-center justify-center cursor-pointer w-full max-w-sm sm:max-w-md md:max-w-2xl lg:max-w-5xl mb-24 bg-[#1F1F1F] border border-[#ADD144] p-6 rounded-lg shadow-2xl mt-3 ${
                    index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
                  }`}
                >
                  {/* Left side: Car Image */}
                  <div className="lg:w-2/3 w-full flex flex-col justify-center items-center p-4 relative">
                    <img
                      src={service.vehicleImageUrl || car}
                      alt="Car"
                      className="h-auto rounded-lg object-contain w-full max-w-[400px] sm:max-w-[500px] lg:max-w-full"
                    />
                    {/* Stats Container */}
                    <div className="flex flex-wrap w-full items-center justify-center gap-4 sm:gap-6 lg:gap-10 border-t border-l border-r border-[#ADD144] shadow-xl p-4 sm:p-6 rounded-lg mt-4 lg:mt-0">
                      {/* Individual Stats */}
                      <div className="flex items-center justify-center gap-4 sm:gap-6">
                        <div className="flex flex-col items-center lg:items-start">
                          <p className="text-[#F0FFC5] text-lg sm:text-xl lg:text-2xl font-semibold">
                          {service.bhp || 'N/A'}
                          </p>
                          <p className="text-[#FFFF] text-sm sm:text-base lg:text-lg">
                            BHP
                          </p>
                        </div>
                        <div className="w-[2px] h-8 sm:h-10 lg:h-12 bg-[#ADD144] hidden sm:block"></div>
                        <div className="flex flex-col items-center lg:items-start">
                          <p className="text-[#F0FFC5] text-lg sm:text-xl lg:text-2xl font-semibold">
                          {service.airbags || 'N/A'}
                          </p>
                          <p className="text-[#FFFF] text-sm sm:text-base lg:text-lg">
                            Airbags
                          </p>
                        </div>
                      </div>
                      <div className="w-[2px] h-8 sm:h-10 lg:h-12 bg-[#ADD144] hidden sm:block"></div>
                      <div className="flex flex-col items-center lg:items-start">
                        <p className="text-[#F0FFC5] text-lg sm:text-xl lg:text-2xl font-semibold">
                        {service.mileage || 'N/A'}
                        </p>
                        <p className="text-[#FFFF] text-sm sm:text-base lg:text-lg">
                          Mileage (kmpl)
                        </p>
                      </div>
                      <div className="w-[2px] h-8 sm:h-10 lg:h-12 bg-[#ADD144] hidden sm:block"></div>
                      <div className="flex flex-col items-center lg:items-start">
                        <p className="text-[#F0FFC5] text-lg sm:text-xl lg:text-2xl font-semibold">
                        {service.ncap || 'N/A'}
                        </p>
                        <p className="text-[#FFFF] text-sm sm:text-base lg:text-lg">
                          NCAP
                        </p>
                      </div>
                    </div>
                  </div>
      
                  {/* Right side: Service Details */}
                  <div className="lg:w-1/3 w-full flex flex-col items-start p-2">
                    <ul className="p-4 w-full">
                      <li className="relative mb-4 text-[#FFFFFF] text-xl sm:text-2xl lg:text-3xl font-semibold pb-2 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-full after:h-[3px] after:bg-[linear-gradient(to_bottom_right,_rgba(240,255,197,0),_rgba(240,255,197,0.2))]">
                        <span>{service.vehicleMake} {service.variant} {service.vehicleModel}</span>
                      </li>
                      <li className="py-2 text-[#FFFFFF] text-sm sm:text-base lg:text-lg">
                        <span className="font-semibold">Booking ID</span>: {service._id}
                      </li>
                      <li className="py-2 text-[#FFFFFF] text-sm sm:text-base lg:text-lg">
                        <span className="font-semibold">VIN</span>: {service.allottedVin}
                      </li>
                      <li className="py-2 text-[#FFFFFF] text-sm sm:text-base lg:text-lg">
                        <span className="font-semibold">Fuel</span>: {service.fuel || 'Not specified'}
                      </li>
                      <li className="py-2 text-[#FFFFFF] text-sm sm:text-base lg:text-lg">
                        <span className="font-semibold">Engine</span>: {service.engine || 'Not specified'}
                      </li>
                      <li className="py-2 text-[#FFFFFF] text-sm sm:text-base lg:text-lg">
                        <span className="font-semibold">Variant</span>: {service.variant}
                      </li>
                      <li className="py-2 text-[#FFFFFF] text-sm sm:text-base lg:text-lg">
                        <span className="font-semibold">Status</span>: {service.status}
                      </li>
                      {service?.customMessage && (
                        <li className="py-2 text-[#FFFFFF] text-sm sm:text-base lg:text-lg">
                          <span className="font-semibold">Message</span>: {service.customMessage}
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-red-500 mt-4">No service details available.</p>
            )}
          </>
        )}
      </div>
      
      )}
      <Footer />
    </div>
  );
};

export default Progress;
